// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';

/**
 * New article (full metadata) or Update article (locked meta + content/summary/change_note).
 * Parent owns form state and prepare/import/save handlers.
 *
 * @param {Object} props
 * @param {'create'|'update'} props.mode
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Record<string, string>} props.formState — create: full fields; update: content, summary, change_note
 * @param {(patch: Record<string, string>) => void} props.onFormPatch
 * @param {null|{ title: string, slug: string, language: string, knowledge_type: string, source_key: string }} props.lockedMeta
 * @param {string|null} props.prepareNotice
 * @param {() => void} props.onDismissPrepareNotice
 * @param {boolean} props.prepareLoading
 * @param {boolean} props.extractLoading
 * @param {React.RefObject<HTMLInputElement|null>} props.fileInputRef
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.onImportFileChange
 * @param {() => void} props.onPrepareClick
 * @param {() => void} props.onSaveClick
 * @param {boolean} props.saveDisabled
 * @param {{ value: string, label: string }[]} props.knowledgeTypeOptions
 * @param {{ prepare_max_input_chars?: number }|null} props.config
 */
function KnowledgeArticleEditorDialog({
  mode,
  open,
  onClose,
  formState,
  onFormPatch,
  lockedMeta,
  prepareNotice,
  onDismissPrepareNotice,
  prepareLoading,
  extractLoading,
  fileInputRef,
  onImportFileChange,
  onPrepareClick,
  onSaveClick,
  saveDisabled,
  knowledgeTypeOptions,
  config,
}) {
  const isUpdate = mode === 'update';
  const contentLen = (formState.content || '').trim().length;
  const prepareDisabled =
    prepareLoading || extractLoading || contentLen < 40;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isUpdate ? 'Update knowledge' : 'New knowledge article'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {isUpdate && (
          <Alert severity="info">
            Title, slug, and type stay the same. <strong>Save &amp; publish</strong> saves a new version and makes it live
            for search and RAG when you have <strong>KNOWLEDGE_PUBLISH</strong>; otherwise the version is saved and can be
            published from the detail panel.
          </Alert>
        )}
        {prepareNotice && (
          <Alert severity="info" onClose={onDismissPrepareNotice}>
            {prepareNotice}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          {isUpdate ? (
            <>
              <strong>Step 1:</strong> Edit body below or <strong>upload</strong> a .docx/.pdf.{' '}
              <strong>Step 2:</strong> Optional <strong>Prepare with AI</strong> refreshes body and summary only (metadata
              stays fixed). <strong>Step 3:</strong>{' '}
              <strong>Save &amp; publish</strong> (live for RAG if you have KNOWLEDGE_PUBLISH).
              {config?.prepare_max_input_chars != null && (
                <>
                  {' '}
                  Max length per prepare run:{' '}
                  <strong>{config.prepare_max_input_chars.toLocaleString()}</strong> characters.
                </>
              )}
            </>
          ) : (
            <>
              <strong>Step 1:</strong> Paste text or <strong>upload</strong> a .docx/.pdf (min. 40 characters).{' '}
              <strong>Step 2:</strong> Click <strong>Prepare with AI</strong> — if you have not chosen a type yet, you will
              pick a <strong>knowledge category</strong> first; Ollama then fills title, slug, summary, and cleaned body for
              review. <strong>Step 3:</strong> Adjust metadata if needed and <strong>Save &amp; publish</strong> (knowledge
              type is required — set automatically after prepare, or choose below; goes live for RAG with KNOWLEDGE_PUBLISH).
              {config?.prepare_max_input_chars != null && (
                <>
                  {' '}
                  Maximum length for one run: <strong>{config.prepare_max_input_chars.toLocaleString()}</strong>{' '}
                  characters — split longer documents into multiple articles.
                </>
              )}
            </>
          )}
        </Typography>
        <input
          type="file"
          ref={fileInputRef}
          hidden
          accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
          onChange={onImportFileChange}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={extractLoading ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={extractLoading || prepareLoading}
          >
            {extractLoading ? 'Extracting…' : 'Upload Word/PDF'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={prepareLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={onPrepareClick}
            disabled={prepareDisabled}
          >
            {prepareLoading ? 'Preparing…' : 'Prepare with AI'}
          </Button>
        </Box>
        <TextField
          label="Paste document text here"
          value={formState.content || ''}
          onChange={(e) => onFormPatch({ content: e.target.value })}
          multiline
          minRows={12}
          fullWidth
          placeholder="Paste PDF copy, Word export, or email thread…"
          autoFocus={!isUpdate}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          {isUpdate ? 'Article metadata (read-only)' : 'Article metadata (edit after AI prepare or enter manually)'}
        </Typography>
        {isUpdate && lockedMeta ? (
          <Box
            sx={{
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography variant="body2">
              <strong>Type:</strong> {lockedMeta.knowledge_type}
            </Typography>
            <Typography variant="body2">
              <strong>Title:</strong> {lockedMeta.title}
            </Typography>
            <Typography variant="body2">
              <strong>Slug:</strong> {lockedMeta.slug}
            </Typography>
            <Typography variant="body2">
              <strong>Language:</strong> {lockedMeta.language}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              <strong>Source key:</strong> {lockedMeta.source_key}
            </Typography>
          </Box>
        ) : (
          <>
            <FormControl fullWidth required size="small">
              <InputLabel id="km-editor-knowledge-type-label">Knowledge type</InputLabel>
              <Select
                labelId="km-editor-knowledge-type-label"
                label="Knowledge type"
                value={formState.knowledge_type || ''}
                onChange={(e) => onFormPatch({ knowledge_type: e.target.value })}
              >
                <MenuItem value="">
                  <em>Select type…</em>
                </MenuItem>
                {knowledgeTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Required before Save &amp; publish. If you use Prepare with AI without choosing here, the category you pick
                in the next step is applied automatically.
              </Typography>
            </FormControl>
            <TextField
              label="Title"
              value={formState.title || ''}
              onChange={(e) => onFormPatch({ title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Slug"
              value={formState.slug || ''}
              onChange={(e) => onFormPatch({ slug: e.target.value })}
              fullWidth
            />
            <TextField
              label="Language"
              value={formState.language || ''}
              onChange={(e) => onFormPatch({ language: e.target.value })}
            />
          </>
        )}
        <TextField
          label="Summary (optional, from AI)"
          value={formState.summary || ''}
          onChange={(e) => onFormPatch({ summary: e.target.value })}
          multiline
          minRows={2}
          fullWidth
          helperText="Stored on the version for editors; not shown in chat RAG context by default."
        />
        {isUpdate && (
          <TextField
            label="Change note (optional)"
            value={formState.change_note || ''}
            onChange={(e) => onFormPatch({ change_note: e.target.value })}
            fullWidth
            inputProps={{ maxLength: 512 }}
            helperText="Short note for this version (e.g. what you updated)."
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSaveClick} disabled={saveDisabled}>
          Save &amp; publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default KnowledgeArticleEditorDialog;
